/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  IconButton,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Description as PdfIcon,
  QuestionAnswer as FaqIcon,
  TextFields as TextIcon,
  Language as UrlIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import UPLOAD_KNOWLEDGE_SOURCE from "src/graphql/mutation/chatbot/uploadKnowledgeSource";
import DELETE_KNOWLEDGE_SOURCE from "src/graphql/mutation/chatbot/deleteKnowledgeSource";
import GET_BOT from "src/graphql/query/chatbot/getBot";
import KNOWLEDGE_SOURCE_PROCESSING from "src/graphql/subscriptions/chatbot/knowledgeSourceProcessing";

interface KnowledgeBaseTabProps {
  botId?: string;
  vectorNamespace?: string;
  onDataAdded?: () => void;
  entityId?: string;
  entityType?: string;
}

const KnowledgeBaseTab: React.FC<KnowledgeBaseTabProps> = ({
  botId,
  onDataAdded,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();

  const [docs, setDocs] = React.useState<any[]>([]);

  const { data: botData, refetch } = useQuery(GET_BOT, {
    variables: { botId },
    fetchPolicy: "network-only",
  });

  // Sync docs with query data
  React.useEffect(() => {
    if (botData?.getBot?.knowledgeSources) {
      setDocs(botData.getBot.knowledgeSources);
    }
  }, [botData]);

  const [uploadSource] = useMutation(UPLOAD_KNOWLEDGE_SOURCE);
  const [deleteSource] = useMutation(DELETE_KNOWLEDGE_SOURCE);

  // Subscription for real-time updates
  useSubscription(KNOWLEDGE_SOURCE_PROCESSING, {
    variables: { botId },
    onData: ({ data: subscriptionResult }) => {
      const update = subscriptionResult.data?.knowledgeSourceProcessing;
      if (!update) return;

      console.log("KNOWLEDGE_PULSE:", update.sourceId, update.status, update);

      setDocs((prev) => {
        const index = prev.findIndex((d) => d.sourceId === update.sourceId);
        if (index > -1) {
          const newDocs = [...prev];
          newDocs[index] = { ...newDocs[index], ...update };
          return newDocs;
        }
        // If it's a new document not yet in the list, add it
        return [update, ...prev];
      });

      if (update.status === "completed") {
        enqueueSnackbar(`Source ${update.sourceName} processed successfully`, {
          variant: "success",
        });
        refetch();
      } else if (update.status === "failed") {
        enqueueSnackbar(
          `Source ${update.sourceName} failed: ${update.errorMessage}`,
          { variant: "error" },
        );
        refetch();
      }
    },
  });

  const [activeTab, setActiveTab] = useState(0);
  const [faqs, setFaqs] = useState<any[]>([]);
  const [customText, setCustomText] = useState("");
  const [urls, setUrls] = useState<any[]>([]);
  const [newUrl, setNewUrl] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "success" | "error"
  >("idle");
  const [uploadMessage, setUploadMessage] = useState("");

  const documents = botData?.getBot?.knowledgeSources || [];

  const MAX_FILES = 5;
  const MAX_SIZE_MB = 10;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const validateFiles = (files: File[]): string | null => {
    if (selectedFiles.length + files.length > MAX_FILES) {
      return `You can only upload a maximum of ${MAX_FILES} files at once.`;
    }

    // for (const file of files) {
    //   if (file.type !== "application/pdf") {
    //     return `File "${file.name}" is not a PDF. Only PDF files are allowed.`;
    //   }
    //   if (file.size > MAX_SIZE_BYTES) {
    //     return `File "${file.name}" exceeds the ${MAX_SIZE_MB}MB size limit.`;
    //   }
    // }

    const invalidFile = files.find((file) => {
      if (file.type !== "application/pdf") return true;
      if (file.size > MAX_SIZE_BYTES) return true;
      return false;
    });

    if (invalidFile) {
      if (invalidFile.type !== "application/pdf") {
        return `File "${invalidFile.name}" is not a PDF. Only PDF files are allowed.`;
      }

      if (invalidFile.size > MAX_SIZE_BYTES) {
        return `File "${invalidFile.name}" exceeds the ${MAX_SIZE_MB}MB size limit.`;
      }
    }

    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      const error = validateFiles(files);

      if (error) {
        setUploadStatus("error");
        setUploadMessage(error);
        return;
      }

      setSelectedFiles((prev) => [...prev, ...files]);
      setUploadStatus("idle");
      setUploadMessage("");
      // Reset input value to allow selecting same file again if needed
      event.target.value = "";
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer.files) {
      const files = Array.from(event.dataTransfer.files);
      const error = validateFiles(files);

      if (error) {
        setUploadStatus("error");
        setUploadMessage(error);
        return;
      }

      setSelectedFiles((prev) => [...prev, ...files]);
      setUploadStatus("idle");
      setUploadMessage("");
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadStatus("idle");
    setUploadMessage("");
  };

  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    setUploadStatus("uploading");
    setUploadMessage("Uploading files...");

    try {
      // for (const file of selectedFiles) {
      //   await uploadSource({
      //     variables: {
      //       input: {
      //         botId,
      //         sourceType: "document",
      //         sourceName: file.name,
      //         file: file,
      //       },
      //     },
      //   });
      // }

      await Promise.all(
        selectedFiles.map((file) =>
          uploadSource({
            variables: {
              input: {
                botId,
                sourceType: "document",
                sourceName: file.name,
                file,
              },
            },
          }),
        ),
      );

      setSelectedFiles([]);
      setUploadStatus("success");
      setUploadMessage("Files uploaded and queuing for processing...");
      refetch();
      setActiveTab(4); // Navigate to Indexed Documents
      if (onDataAdded) onDataAdded();

      setTimeout(() => {
        setUploadStatus("idle");
        setUploadMessage("");
      }, 3000);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadStatus("error");
      setUploadMessage("Failed to upload files. Please check your connection.");
    }
  };

  const handleAddFaq = () => {
    setFaqs([...faqs, { question: "", answer: "" }]);
  };

  const handleRemoveFaq = (index: number) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  const handleFaqChange = (
    index: number,
    field: "question" | "answer",
    value: string,
  ) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  const handleSaveFaqs = async () => {
    try {
      await uploadSource({
        variables: {
          input: {
            botId,
            sourceType: "faq",
            sourceName: "FAQ Entry",
            text: JSON.stringify(faqs),
          },
        },
      });
      enqueueSnackbar("FAQs queued for indexing", { variant: "success" });
      setFaqs([]);
      refetch();
      setActiveTab(4); // Navigate to Indexed Documents
      if (onDataAdded) onDataAdded();
    } catch (error) {
      enqueueSnackbar("Failed to save FAQs", { variant: "error" });
    }
  };

  const handleSaveCustomText = async () => {
    try {
      await uploadSource({
        variables: {
          input: {
            botId,
            sourceType: "text",
            sourceName: "Custom Text Input",
            text: customText,
          },
        },
      });
      enqueueSnackbar("Custom text queued for indexing", {
        variant: "success",
      });
      setCustomText("");
      refetch();
      setActiveTab(4); // Navigate to Indexed Documents
      if (onDataAdded) onDataAdded();
    } catch (error) {
      enqueueSnackbar("Failed to save text", { variant: "error" });
    }
  };

  const isValidUrl = (url: string): boolean => {
    try {
      // Basic check if it has a protocol, if not add it for the check
      const urlToCheck = url.match(/^[a-zA-Z]+:\/\//) ? url : `https://${url}`;
      // new URL(urlToCheck);
      // return true;
      const parsed = new URL(urlToCheck);
      return Boolean(parsed);
    } catch {
      return false;
    }
  };

  const handleAddUrl = () => {
    let urlToAdd = newUrl.trim();
    if (!urlToAdd) return;

    // Auto-prefix with https if protocol is missing
    if (!urlToAdd.match(/^[a-zA-Z]+:\/\//)) {
      urlToAdd = `https://${urlToAdd}`;
    }

    if (!isValidUrl(urlToAdd)) {
      enqueueSnackbar("Please enter a valid URL", { variant: "error" });
      return;
    }

    // Check for duplicates
    if (urls.some((u) => u.url === urlToAdd)) {
      enqueueSnackbar("This URL has already been added", {
        variant: "warning",
      });
      return;
    }

    setUrls([...urls, { url: urlToAdd, status: "pending" }]);
    setNewUrl("");
  };

  const handleRemoveUrl = (index: number) => {
    setUrls(urls.filter((_, i) => i !== index));
  };

  const handleScrapeUrls = async () => {
    try {
      // for (const urlItem of urls) {
      //   await uploadSource({
      //     variables: {
      //       input: {
      //         botId,
      //         sourceType: "url",
      //         sourceName: urlItem.url,
      //         url: urlItem.url,
      //       },
      //     },
      //   });
      // }

      await Promise.all(
        urls.map((urlItem) =>
          uploadSource({
            variables: {
              input: {
                botId,
                sourceType: "url",
                sourceName: urlItem.url,
                url: urlItem.url,
              },
            },
          }),
        ),
      );

      enqueueSnackbar("URLs queued for scraping", { variant: "success" });
      setUrls([]);
      refetch();
      setActiveTab(4); // Navigate to Indexed Documents
      if (onDataAdded) onDataAdded();
    } catch (error) {
      enqueueSnackbar("Failed to queue URLs for scraping", {
        variant: "error",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "processing":
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    if (!window.confirm("Are you sure you want to delete this source?")) return;
    try {
      await deleteSource({ variables: { sourceId } });
      enqueueSnackbar("Source deleted", { variant: "success" });
      refetch();
    } catch (error) {
      enqueueSnackbar("Failed to delete source", { variant: "error" });
    }
  };

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(e: React.SyntheticEvent, val: number) => setActiveTab(val)}
        sx={{
          mb: 3,
          "& .MuiTabs-flexContainer": {
            flexDirection: isMobile ? "column" : "row",
          },
          "& .MuiTab-root": {
            alignItems: isMobile ? "flex-start" : "center",
            textAlign: "left",
            minHeight: isMobile ? 48 : 72,
          },
          borderBottom: isMobile ? 0 : 1,
          borderRight: isMobile ? 1 : 0,
          borderColor: "divider",
        }}
        orientation={isMobile ? "vertical" : "horizontal"}
        variant={isMobile ? "standard" : "scrollable"}
        scrollButtons="auto"
      >
        <Tab label="PDFs" icon={<PdfIcon />} iconPosition="start" />
        <Tab label="FAQs" icon={<FaqIcon />} iconPosition="start" />
        <Tab label="Text" icon={<TextIcon />} iconPosition="start" />
        <Tab label="URLs" icon={<UrlIcon />} iconPosition="start" />
        <Tab label="Indexed Documents" />
      </Tabs>

      {/* PDF Upload Tab */}
      {activeTab === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Upload PDF Documents
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Upload PDF files to add to the knowledge base. Files will be
            processed and indexed automatically.
          </Typography>

          {uploadStatus === "error" && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadMessage}
            </Alert>
          )}

          {uploadStatus === "success" && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {uploadMessage}
            </Alert>
          )}

          <input
            type="file"
            accept="application/pdf"
            multiple
            style={{ display: "none" }}
            id="pdf-upload-input"
            onChange={handleFileSelect}
          />
          <label htmlFor="pdf-upload-input">
            <Paper
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                p: 4,
                mt: 2,
                border: "2px dashed",
                borderColor: "divider",
                textAlign: "center",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              <UploadIcon
                sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="body1" gutterBottom>
                Drag and drop PDF files here, or click to browse
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Maximum file size: {MAX_SIZE_MB}MB | Maximum {MAX_FILES} files
                at once
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                >
                  Select Files
                </Button>
              </Box>
            </Paper>
          </label>

          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Files ({selectedFiles.length})
              </Typography>
              <List>
                {selectedFiles.map((file, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 1,
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveFile(index)}
                        disabled={uploadStatus === "uploading"}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <PdfIcon color="error" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="body2">{file.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleUploadFiles}
                  disabled={uploadStatus === "uploading"}
                  startIcon={
                    uploadStatus === "uploading" ? undefined : <UploadIcon />
                  }
                >
                  {uploadStatus === "uploading"
                    ? "Uploading..."
                    : "Upload PDFs"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* FAQ Entry Tab */}
      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Add FAQ Pairs
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Add question and answer pairs to help the bot respond to common
            questions.
          </Typography>

          <List sx={{ mt: 2 }}>
            {faqs.map((faq, index) => (
              <ListItem
                key={index}
                sx={{
                  display: "block",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 2,
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      mr: { xs: 0, sm: 2 },
                      gap: 1,
                    }}
                  >
                    <TextField
                      label="Question"
                      value={faq.question}
                      onChange={(e) =>
                        handleFaqChange(index, "question", e.target.value)
                      }
                      fullWidth
                      size="small"
                      margin="normal"
                    />
                    <TextField
                      label="Answer"
                      value={faq.answer}
                      onChange={(e) =>
                        handleFaqChange(index, "answer", e.target.value)
                      }
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      margin="normal"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: { xs: 1, sm: 0 },
                    }}
                  >
                    <IconButton
                      onClick={() => handleRemoveFaq(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              onClick={handleAddFaq}
              startIcon={<AddIcon />}
              variant="outlined"
            >
              Add FAQ
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveFaqs}
              disabled={faqs.length === 0}
            >
              Save & Index FAQs
            </Button>
          </Box>
        </Box>
      )}

      {/* Manual Text Tab */}
      {activeTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Add Custom Text Content
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Paste any text content you want the bot to know about.
          </Typography>

          <TextField
            label="Custom Text"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            fullWidth
            multiline
            rows={10}
            margin="normal"
            placeholder="Paste your content here...

This could be company policies, product information, or any other text you want the bot to know about."
            helperText={`${customText.length} characters`}
          />

          <Button
            variant="contained"
            onClick={handleSaveCustomText}
            disabled={!customText}
            sx={{ mt: 2 }}
          >
            Save & Index
          </Button>
        </Box>
      )}

      {/* URL Scraping Tab */}
      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Scrape Web Pages
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Add URLs to scrape content from web pages. The content will be
            extracted and indexed into the knowledge base.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mt: 3,
              mb: 3,
              alignItems: { xs: "stretch", sm: "flex-start" },
            }}
          >
            <TextField
              label="Website URL"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleAddUrl();
                }
              }}
              fullWidth
              placeholder="https://example.com/page"
              helperText="Enter a valid URL starting with http:// or https://"
            />
            <Button
              onClick={handleAddUrl}
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{
                minWidth: 120,
                height: 56,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Add URL
            </Button>
          </Box>

          {urls.length > 0 && (
            <>
              {!isMobile ? (
                <TableContainer
                  component={Paper}
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>URL</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {urls.map((urlItem, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <UrlIcon fontSize="small" color="action" />
                              <Typography
                                variant="body2"
                                sx={{ wordBreak: "break-all" }}
                              >
                                {urlItem.url}
                              </Typography>
                            </Box>
                            {urlItem.error && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                Error: {urlItem.error}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={urlItem.status}
                              color={
                                urlItem.status === "completed"
                                  ? "success"
                                  : urlItem.status === "scraping"
                                  ? "warning"
                                  : urlItem.status === "failed"
                                  ? "error"
                                  : "default"
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleRemoveUrl(index)}
                              color="error"
                              size="small"
                              disabled={urlItem.status === "scraping"}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {urls.map((urlItem, index) => (
                    <Paper
                      key={index}
                      sx={{ p: 2, border: "1px solid", borderColor: "divider" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <UrlIcon fontSize="small" color="action" />
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: "bold" }}
                          >
                            URL
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => handleRemoveUrl(index)}
                          color="error"
                          size="small"
                          disabled={urlItem.status === "scraping"}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: "break-all", mb: 2 }}
                      >
                        {urlItem.url}
                      </Typography>
                      {urlItem.error && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mb: 1, display: "block" }}
                        >
                          Error: {urlItem.error}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          Status
                        </Typography>
                        <Chip
                          label={urlItem.status}
                          color={
                            urlItem.status === "completed"
                              ? "success"
                              : urlItem.status === "scraping"
                              ? "warning"
                              : urlItem.status === "failed"
                              ? "error"
                              : "default"
                          }
                          size="small"
                        />
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              <Button
                variant="contained"
                onClick={handleScrapeUrls}
                disabled={
                  urls.length === 0 || urls.some((u) => u.status === "scraping")
                }
                fullWidth={isMobile}
                sx={{ mt: 2 }}
              >
                Scrape & Index URLs
              </Button>
            </>
          )}

          {urls.length === 0 && (
            <Paper
              sx={{
                p: 4,
                border: "1px dashed",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <UrlIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
              <Typography variant="body2" color="textSecondary">
                No URLs added yet. Add URLs above to scrape web content.
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Indexed Documents Tab */}
      {activeTab === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Indexed Documents
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            All documents that have been indexed in this knowledge base.
          </Typography>

          {!isMobile ? (
            <TableContainer
              component={Paper}
              sx={{
                mt: 2,
                width: "100%",
                overflowX: "auto",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Document</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Chunks</TableCell>
                    <TableCell>Indexed At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ py: 4 }}
                        >
                          No documents indexed yet. Upload PDFs, add FAQs, add
                          custom text, or scrape URLs to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    docs.map((doc: any) => (
                      <TableRow key={doc.id}>
                        <TableCell>{doc.sourceName}</TableCell>
                        <TableCell>{doc.sourceType}</TableCell>
                        <TableCell>
                          <Chip
                            label={doc.status}
                            color={getStatusColor(doc.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{doc.totalChunks || 0}</TableCell>
                        <TableCell>
                          {new Date(doc.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSource(doc.sourceId)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              {documents.length === 0 ? (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: "center",
                    border: "1px dashed",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    No documents indexed yet.
                  </Typography>
                </Paper>
              ) : (
                docs.map((doc: any) => (
                  <Paper
                    key={doc.id}
                    sx={{ p: 2, border: "1px solid", borderColor: "divider" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box sx={{ flex: 1, pr: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold", wordBreak: "break-all" }}
                        >
                          {doc.sourceName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {doc.sourceType}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteSource(doc.sourceId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          Status
                        </Typography>
                        <Chip
                          label={doc.status}
                          color={getStatusColor(doc.status) as any}
                          size="small"
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          Chunks
                        </Typography>
                        <Typography variant="body2">
                          {doc.totalChunks || 0}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          Indexed At
                        </Typography>
                        <Typography variant="body2">
                          {new Date(doc.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default KnowledgeBaseTab;
